/**
 * The page primitives the protocol document is drawn with: the sheet it
 * writes to, its geometry, and text that wraps, clips and breaks pages. Split
 * out of `protocolPdf.ts`, which composes the document itself.
 */
import { rgb, type PDFDocument, type PDFFont, type PDFImage, type PDFPage } from "pdf-lib";
import { toWinAnsi, wrapText } from "@/domains/documents/utils/templateFormPdfRenderer";
import type { ProtocolPdfTheme } from "@/domains/pm/utils/protocolPdfTheme";
import type { ProtocolLinks } from "@/domains/pm/utils/protocolPdfLinks";

export const PAGE_WIDTH = 595.28;
export const PAGE_HEIGHT = 841.89;
export const MARGIN = 42;
const FOOTER_HEIGHT = 20;
export const CONTENT_WIDTH = PAGE_WIDTH - MARGIN * 2;
export const TEXT = rgb(0.1, 0.11, 0.13);
export const MUTED = rgb(0.4, 0.42, 0.46);
export const RULE = rgb(0.82, 0.83, 0.85);
export const SURFACE = rgb(0.96, 0.965, 0.97);
export const ACCENT = rgb(0.73, 0.08, 0.17);

export interface PdfSheet {
  doc: PDFDocument;
  page: PDFPage;
  y: number;
  regular: PDFFont;
  bold: PDFFont;
  logo: PDFImage | null;
  theme: ProtocolPdfTheme;
  /** Pages carrying an attached PDF's own pages. They are numbered with the
   *  rest but get no footer — it would print over somebody else's drawing. */
  foreignPages: Set<PDFPage>;
  /** Links and paperclips collected while drawing, joined at the end. */
  links: ProtocolLinks;
}

export function addPage(sheet: PdfSheet): void {
  sheet.page = sheet.doc.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
  sheet.y = PAGE_HEIGHT - MARGIN;
}

/** What a fresh page can hold between the header margin and the footer. */
const MAX_CONTENT_HEIGHT = PAGE_HEIGHT - MARGIN * 2 - FOOTER_HEIGHT;

export function ensureSpace(sheet: PdfSheet, height: number): boolean {
  // A reservation taller than a whole page cannot be honoured by breaking:
  // asking for it outright would add a page that still does not fit it, and
  // leave the one before it half empty. Reserve at most a page.
  if (sheet.y - Math.min(height, MAX_CONTENT_HEIGHT) >= MARGIN + FOOTER_HEIGHT) return false;
  addPage(sheet);
  return true;
}

export function drawText(
  sheet: PdfSheet,
  value: string,
  x: number,
  y: number,
  options: { font?: PDFFont; size?: number; color?: ReturnType<typeof rgb> } = {}
): void {
  sheet.page.drawText(toWinAnsi(value), {
    x,
    y,
    font: options.font ?? sheet.regular,
    size: options.size ?? 10,
    color: options.color ?? TEXT,
  });
}

/**
 * Clip a single-line value to `width`, ending it with an ellipsis. Building and
 * project names are free text, and `drawText` neither wraps nor clips — a long
 * one would run off the right edge of the page.
 */
export function ellipsise(value: string, font: PDFFont, size: number, width: number): string {
  const text = toWinAnsi(value);
  if (font.widthOfTextAtSize(text, size) <= width) return text;
  let clipped = text;
  while (clipped.length > 0 && font.widthOfTextAtSize(`${clipped}…`, size) > width) {
    clipped = clipped.slice(0, -1);
  }
  // A column narrower than the ellipsis itself gets nothing: drawText neither
  // wraps nor clips, so returning "…" there would overflow the very width this
  // function exists to respect.
  if (clipped.length === 0) return font.widthOfTextAtSize("…", size) <= width ? "…" : "";
  return `${clipped}…`;
}

/** How many lines of `lineHeight` fit a fresh page below `chrome` points of it. */
export function linesPerPage(lineHeight: number, chrome: number): number {
  return Math.max(1, Math.floor((PAGE_HEIGHT - MARGIN * 2 - FOOTER_HEIGHT - chrome) / lineHeight));
}

/**
 * Clip wrapped text to what a single page can hold. `ensureSpace` only ever
 * starts ONE fresh page, so a block taller than a whole page keeps drawing at
 * ever lower baselines — over the footer, then off the sheet — and those lines
 * are simply lost. Issue titles and activity text are unbounded user input (a
 * pasted paragraph as a title is enough), so the overflow ends in an ellipsis
 * rather than in nothing.
 */
export function capLines(
  lines: string[],
  font: PDFFont,
  size: number,
  width: number,
  maxLines: number
): string[] {
  if (lines.length <= maxLines) return lines;
  const kept = lines.slice(0, maxLines);
  kept[maxLines - 1] = ellipsise(`${kept[maxLines - 1]}…`, font, size, width);
  return kept;
}

/**
 * A printed section's heading — Summary, Comments, Activity, Attachments — in
 * the layout's size and colour, with its accent bar when the layout has one.
 * `baseline` is where the text sits; the bar hangs 6pt under it.
 */
export function drawSectionHeading(
  sheet: PdfSheet,
  value: string,
  baseline: number,
  size: number = sheet.theme.sectionHeadingSize
): void {
  const { sectionHeadingColor, sectionRule } = sheet.theme;
  drawText(sheet, value, MARGIN, baseline, {
    font: sheet.bold,
    size,
    color: sectionHeadingColor,
  });
  if (!sectionRule) return;
  sheet.page.drawRectangle({
    x: MARGIN,
    y: baseline - 7,
    width: 28,
    height: 2,
    color: sectionRule,
  });
}

export function drawRule(sheet: PdfSheet, y: number): void {
  sheet.page.drawLine({
    start: { x: MARGIN, y },
    end: { x: PAGE_WIDTH - MARGIN, y },
    thickness: 0.6,
    color: RULE,
  });
}

/** Draw already-wrapped lines from `top` downwards; returns the height used. */
export function drawLines(
  sheet: PdfSheet,
  lines: string[],
  x: number,
  top: number,
  options: { font?: PDFFont; size?: number; lineHeight?: number; color?: ReturnType<typeof rgb> }
): number {
  const size = options.size ?? 10;
  const lineHeight = options.lineHeight ?? size * 1.3;
  lines.forEach((line, index) => {
    drawText(sheet, line, x, top - size - index * lineHeight, {
      font: options.font,
      size,
      color: options.color,
    });
  });
  return Math.max(lines.length, 1) * lineHeight;
}

export function drawWrapped(
  sheet: PdfSheet,
  value: string,
  x: number,
  top: number,
  width: number,
  options: {
    font?: PDFFont;
    size?: number;
    lineHeight?: number;
    color?: ReturnType<typeof rgb>;
  } = {}
): number {
  const font = options.font ?? sheet.regular;
  const size = options.size ?? 10;
  const lineHeight = options.lineHeight ?? size * 1.3;
  const lines = capLines(
    wrapText(value, font, size, width),
    font,
    size,
    width,
    linesPerPage(lineHeight, 0)
  );
  return drawLines(sheet, lines, x, top, { ...options, font, size, lineHeight });
}
